import React, { ReactNode } from "react";
import { Button } from "antd";

type ButtonProps = {
  title?: string | ReactNode;
  className?: string;
  variant?: "link" | "text" | "dashed" | "outlined" | "solid" | "filled";
  shape?: "round" | "circle";
  type?: "primary" | "link" | "text" | "default" | "dashed";
  btnColor?: "black" | "blue" | "success" | "danger" | "white" | "secondary" | "redButton" | "outline";
  isLoading?: boolean;
  disabled?: boolean;
  icon?: ReactNode;
  iconPosition?: "left" | "right";
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  htmlType?: "button" | "submit" | "reset";
  children?: ReactNode;
  textClassName?: string;
};

const btnColorClasses: Record<string, string> = {
  primary:
    "!bg-[#111827] !text-white hover:!bg-[#1F2937]/80 hover:!border-none hover:!outline-none",
  black: "!bg-[#001630] !text-white hover:!bg-gray-900",
  white: "!bg-white !text-black !border !border-gray-300 ",
  success:
    "!bg-[#16A34A] !text-white hover:!bg-[#15803D] hover:!shadow-[0_6px_16px_-6px_rgba(22,163,74,0.45)] hover:!-translate-y-[1px] active:!translate-y-0 active:!shadow-none !border-none hover:!outline-none",
  danger:
    "!bg-gradient-to-r !from-[#DC2626] !to-[#B91C1C] !text-white hover:!from-[#B91C1C] hover:!to-[#991B1B] hover:!shadow-[0_6px_16px_-6px_rgba(220,38,38,0.5)] hover:!-translate-y-[1px] active:!translate-y-0 active:!shadow-none !border-none",
  blue: "!bg-[#9333EA] !text-white hover:!bg-[#7E22CE]/80 hover:!border-none hover:!outline-none",
  redButton:
    "!bg-[#DC2626] !text-white hover:!bg-[#B91C1C] hover:!shadow-[0_6px_16px_-6px_rgba(220,38,38,0.5)] hover:!-translate-y-[1px] active:!translate-y-0 active:!shadow-none !border-none hover:!outline-none",
  secondary: "!bg-[#F9FAFB] !text-[#1F2937]  hover:!outline-none",
  outline: "!bg-white !text-[#DC2626] !border !border-[#DC2626] !font-semibold",
};

const CustomButton: React.FC<ButtonProps> = ({
  title,
  shape,
  iconPosition = "left",
  className = "",
  variant,
  type,
  onClick = () => {},
  htmlType = "submit",
  icon = null,
  btnColor,
  isLoading = false,
  disabled = false,
  children,
  textClassName = "",
}) => {
  const resolvedType: ButtonProps["type"] = btnColor ? "default" : type;
  return (
    <Button
      onClick={onClick}
      htmlType={htmlType}
      type={resolvedType}
      disabled={disabled}
      variant={variant}
      shape={shape}
      loading={isLoading}
      className={`min-w-[50%] pl-[16px] pr-[16px] !font-medium !rounded-[10px] !h-[48px] flex items-center gap-2 transition-all duration-200 ease-out will-change-transform
        ${btnColor ? btnColorClasses[btnColor] : ""} ${className}`}
    >
      {icon && iconPosition === "left" && (
        <span className="flex items-center gap-2">{icon}</span>
      )}
      {title && <span className={`${textClassName}`}>{title}</span>}
      {children}
      {icon && iconPosition === "right" && (
        <span className="flex items-center gap-2">{icon}</span>
      )}
    </Button>
  );
};

export default CustomButton;
